import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Inbox, Download, Search, Mail, Phone, Trash2, ArrowUpRight, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap, downloadFile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Label } from '@/components/ui/Input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';

type MessageStatus = 'new' | 'read' | 'archived' | 'spam';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  topic: string | null;
  message: string;
  fields: Record<string, string> | null;
  status: MessageStatus;
  notes: string | null;
  ip_address: string | null;
  geo: { label?: string; country?: string; city?: string } | null;
  created_at: string;
}

const FIELD_LABELS: Record<string, string> = {
  nombre: 'Nombre',
  email: 'Email',
  telefono: 'Teléfono',
  empresa: 'Negocio',
  web: 'Página web',
  sector: 'Sector',
  servicio: 'Necesidad',
  facturacion: 'Facturación',
  mensaje: 'Objetivo',
};

const HIDDEN_FIELDS = new Set(['nombre', 'email', 'telefono', 'mensaje']);

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

const STATUS_STYLES: Record<MessageStatus, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }> = {
  new: { label: 'Nuevo', variant: 'default' },
  read: { label: 'Leído', variant: 'secondary' },
  archived: { label: 'Archivado', variant: 'success' },
  spam: { label: 'Spam', variant: 'destructive' },
};

export const Route = createFileRoute('/_app/messages')({
  component: MessagesPage,
});

function MessagesPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<MessageStatus | ''>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ContactMessage | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contact-messages', q, status, page],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '25', page: String(page) });
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      return unwrap<Paginated<ContactMessage>>(api.get(`/contact-messages?${params}`));
    },
  });

  async function exportCsv() {
    try {
      await downloadFile('/contact-messages/export', 'contact-messages.csv');
    } catch {
      toast.error('No se pudo exportar el CSV');
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Inbox className="size-7 text-neutral-700" /> Mensajes de contacto
          </h1>
          <p className="text-sm text-neutral-500">
            Mensajes enviados desde el formulario de contacto del sitio.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv}>
          <Download className="size-4" /> Exportar CSV
        </Button>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Bandeja</CardTitle>
              <CardDescription>{data?.total ?? 0} mensajes en total.</CardDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as MessageStatus | ''); setPage(1); }}
                className="h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                <option value="">Todos los estados</option>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar nombre/email/asunto" className="pl-9" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Remitente</th>
                <th className="px-4 py-2">Asunto</th>
                <th className="px-4 py-2">Recibido</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {data?.data.length === 0 && !isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Aún no hay mensajes.</td></tr>
              )}
              {data?.data.map((m) => {
                const s = STATUS_STYLES[m.status];
                return (
                  <tr
                    key={m.id}
                    onClick={() => setSelected(m)}
                    className="cursor-pointer border-b border-neutral-100 hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3"><Badge variant={s.variant}>{s.label}</Badge></td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1"><Mail className="size-3" />{m.email}</span>
                        {m.phone && <span className="inline-flex items-center gap-1"><Phone className="size-3" />{m.phone}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{m.topic ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {new Date(m.created_at).toLocaleString('es')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ArrowUpRight className="size-4 text-neutral-400" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <MessageDialog message={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function MessageDialog({ message, onClose }: { message: ContactMessage | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<MessageStatus>('new');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (message) {
      setStatus(message.status);
      setNotes(message.notes ?? '');
    }
  }, [message]);

  const save = useMutation({
    mutationFn: () => api.patch(`/contact-messages/${message!.id}`, { status, notes }),
    onSuccess: () => {
      toast.success('Mensaje actualizado');
      void qc.invalidateQueries({ queryKey: ['contact-messages'] });
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/contact-messages/${message!.id}`),
    onSuccess: () => {
      toast.success('Mensaje eliminado');
      void qc.invalidateQueries({ queryKey: ['contact-messages'] });
      onClose();
    },
  });

  return (
    <Dialog open={!!message} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{message?.name}</DialogTitle>
          <DialogDescription>
            Recibido {message && new Date(message.created_at).toLocaleString('es')}
          </DialogDescription>
        </DialogHeader>
        {message && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <DetailRow icon={Mail} label="Email" value={message.email} />
              <DetailRow icon={Phone} label="Teléfono" value={message.phone} />
              {message.geo?.label && (
                <DetailRow icon={MapPin} label="Ubicación (aprox.)" value={message.geo.label} />
              )}
            </div>
            {message.topic && (
              <div className="text-sm">
                <span className="text-[10px] uppercase tracking-wider text-neutral-500">Necesidad</span>
                <div className="font-medium">{message.topic}</div>
              </div>
            )}
            {message.fields &&
              Object.entries(message.fields)
                .filter(([k, v]) => !HIDDEN_FIELDS.has(k) && k !== 'servicio' && v)
                .map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {FIELD_LABELS[k] ?? k}
                    </span>
                    <div className="font-medium">{v}</div>
                  </div>
                ))}
            <div className="rounded-md border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">Mensaje</div>
              <p className="whitespace-pre-wrap text-sm text-neutral-800">{message.message}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="msg-status">Estado</Label>
              <select
                id="msg-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as MessageStatus)}
                className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="msg-notes">Notas internas</Label>
              <Textarea
                id="msg-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Observaciones, seguimiento…"
              />
            </div>

            <a
              href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.topic ?? 'Tu consulta')}`}
              className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
            >
              <Mail className="size-3" /> Responder por email
            </a>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            className="mr-auto text-red-600 hover:text-red-700"
          >
            <Trash2 className="size-4" /> Eliminar
          </Button>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({
  icon: Icon, label, value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | null;
}) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2">
      <Icon className="mt-0.5 size-4 text-neutral-400" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
        <div className="text-sm font-medium">{value ?? '—'}</div>
      </div>
    </div>
  );
}
